import { Injectable, OnDestroy } from '@angular/core';
import { firstValueFrom, from, interval, Observable, ReplaySubject, Subscription } from 'rxjs';
import { filter, switchMap, tap } from 'rxjs/operators';
import { RestfulEndpoint } from '../../../services/restful-endpoint.service';
import { NavigationEnd, Router } from '@angular/router';
import {
  MarkAllReadResponse,
  MetaData,
  NotificationResponse,
  Notifications,
} from './notifications.model';
import { TranslocoService } from '@ngneat/transloco';
import { ToastrService } from 'ngx-toastr';
import { environment } from '../../../../environments/environment';
import { GlobalService } from '../../../services/global.service';
import { UserService } from 'app/core/user/user.service';
import { AccessLevel } from 'app/core/user/user.model';
import { ProjectsService } from '../../../services/projects.service';

@Injectable({
  providedIn: 'root',
})
export class NotificationsService implements OnDestroy {
  // Local state to hold notifications and metadata
  private _notificationsList: Notifications[] = [];
  private _notifications: ReplaySubject<NotificationResponse> =
    new ReplaySubject<NotificationResponse>(1);
  public _metaData: MetaData = {} as MetaData;

  // Track if this is the first load
  private firstLoad: boolean = true;
  private pageUrl: string;
  private userId: string;

  // Subscription to manage the periodic fetching interval
  private fetchIntervalSubscription: Subscription;
  private fetchInProgress: boolean = false; // Flag to track fetch status

  constructor(
    private endPoint: RestfulEndpoint,
    private _router: Router,
    private _transloco: TranslocoService,
    private user: UserService,
    private _toaster: ToastrService,
    private _globalService: GlobalService,
    private projectsService: ProjectsService,
  ) {
    // Set pageUrl and handle route change detection
    this.pageUrl = window.location.pathname;
    this._router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((data: NavigationEnd) => {
        this.pageUrl = data.urlAfterRedirects;
      });
    // Initialize notifications fetching process
    this.initialize();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Observable to listen to notifications changes
   */
  get notifications$(): Observable<NotificationResponse> {
    return this._notifications.asObservable();
  }

  /**
   * Fetches notifications from the server and updates the local list.
   * This method returns an Observable of the NotificationResponse.
   */
  getNotifications(page: number = 1, isScroll: boolean = false): Observable<NotificationResponse> {
    // Return an empty observable if a fetch is already in progress
    if (this.fetchInProgress) return new Observable<NotificationResponse>();

    // Set fetchInProgress to true to block other fetches
    this.fetchInProgress = true;
    this._notificationsList = [];
    return from(this.user.getUserId()).pipe(
      switchMap((userId) => {
        return this.endPoint.get(`users/${userId}/notifications-web`, { page }).pipe(
          tap((response: NotificationResponse) => {
            this._metaData = response.meta_data;
            this.processNotifications(response.data, isScroll);
            this._notifications.next({
              ...response,
              data: [...this._notificationsList],
            });
            this.fetchInProgress = false; // Reset flag after successful fetch
          }),
          // Reset fetchInProgress flag on error as well
          tap({ error: () => (this.fetchInProgress = false) }),
        );
      }),
    );
  }

  /**
   * Marks a single notification as read by ID and returns an Observable.
   */
  markAsRead(userId: string, notificationId: string): Observable<any> {
    return this.endPoint.post(
      `v2/users/${userId}/notifications/${notificationId}/mark-as-read`,
      {},
    );
  }

  /**
   * Marks all notifications as read and refetches the updated list.
   * This method returns an Observable of the MarkAllReadResponse.
   */
  markAllRead(userId: string): Observable<MarkAllReadResponse> {
    return this.endPoint
      .post(`v2/users/${userId}/notifications/mark-as-read`, { all_events: 1 })
      .pipe(
        tap(() => {
          this.clearNotifications(); // Clear notifications locally
          this.fetchNotifications(); // Refetch notifications
        }),
      );
  }

  async resolveNotificationTargetUrl(notification: Notifications): Promise<string> {
    const rawUrl = notification?.data?.url?.toString() ?? '/notifications';
    let parsedUrl: URL;

    try {
      parsedUrl = new URL(rawUrl, window.location.origin);
    } catch {
      return Promise.resolve(rawUrl);
    }

    const isSameOrigin = parsedUrl.origin === window.location.origin;
    if (!isSameOrigin) {
      return parsedUrl.toString();
    }

    const pathName = this.normalizeLegacyProjectPath(parsedUrl.pathname);
    const projectId = this.extractProjectId(notification, pathName);
    const cloneRequestId = this.extractCloneRequestId(notification, parsedUrl);
    const syncContext = this.extractSyncContext(notification);
    const notificationDataType = this.getNotificationDataType(notification);

    if (this.isCloneRequestNotification(notification, pathName) && projectId) {
      const canOpenCloneRequests = await this.canOpenCloneRequests(notification, projectId);
      if (!canOpenCloneRequests) {
        return `/project-d/${projectId}/dashboard`;
      }

      const cloneRequestsUrl = new URL(
        `/project-d/${projectId}/clone-requests`,
        window.location.origin,
      );
      if (cloneRequestId) {
        cloneRequestsUrl.searchParams.set('requestId', cloneRequestId);
      }
      return `${cloneRequestsUrl.pathname}${cloneRequestsUrl.search}`;
    }

    if (this.isSyncUpdateNotification(notification, pathName) && projectId) {
      const floorsUrl = new URL(`/project-d/${projectId}/floors`, window.location.origin);
      floorsUrl.searchParams.set('syncUpdate', '1');
      if (syncContext?.changeType) {
        floorsUrl.searchParams.set('syncEventType', syncContext.changeType);
      }
      if (syncContext?.entityType) {
        floorsUrl.searchParams.set('syncEntityType', syncContext.entityType);
      }
      if (notificationDataType === 'sync_batch') {
        floorsUrl.searchParams.set('syncBatch', '1');
      }

      return `${floorsUrl.pathname}${floorsUrl.search}`;
    }

    return `${pathName}${parsedUrl.search}${parsedUrl.hash}`;
  }

  private normalizeLegacyProjectPath(pathname: string): string {
    const match = pathname.match(/^\/projects\/([a-zA-Z0-9-]+)(\/.*)?$/);
    if (!match) {
      return pathname;
    }

    const id = match[1];
    const rest = match[2] ?? '';
    return `/project-d/${id}${rest}`;
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Private methods
  // -----------------------------------------------------------------------------------------------------

  // Periodically fetch notifications if denied or not granted
  // Periodically fetch notifications if denied or not granted
  private startPeriodicFetching(): void {
    this.stopPeriodicFetching(); // Clear any existing intervals
    this.fetchIntervalSubscription = interval(environment.notificationTimeInterval) // 30 seconds
      .pipe(
        filter(() => !this.fetchInProgress), // Only proceed if no fetch is in progress
        switchMap(() => this.getNotifications()),
      )
      .subscribe({
        next: () => {
          // Notifications fetched successfully in periodic interval
        },
        error: (error) => {
          // this._toaster.error(
          //   error.message || this._transloco.translate('Something went wrong, please try again.'),
          // );
        },
      });
  }

  // Stop the periodic fetching interval
  private stopPeriodicFetching(): void {
    if (this.fetchIntervalSubscription) {
      this.fetchIntervalSubscription.unsubscribe();
    }
  }

  /**
   * Initializes the notifications fetching process.
   */
  initialize(): void {
    if (localStorage.getItem('access_token')) {
      this._notificationsList = [];
      this._notifications.next(null);
      this.checkNotificationPermission();
      this.fetchNotifications();
    }
  }
  /**
   * Check notification permission and start interval if denied/not granted.
   */
  private checkNotificationPermission(): void {
    if (this._globalService.isNotificationsSupported()) {
      if (
        Notification.permission === 'denied' ||
        Notification.permission === 'default' ||
        Notification.permission === undefined
      ) {
        this.startPeriodicFetching(); // Start periodic fetching if denied/not granted
      } else {
        this.stopPeriodicFetching(); // Stop periodic fetching if granted
      }
    } else {
      console.warn('Notification API is not available on this browser or device.');
      this.startPeriodicFetching(); // Fallback to periodic fetching
    }
  }

  /**
   * Fetches notifications and handles errors.
   */
  private fetchNotifications(): void {
    this.getNotifications(1).subscribe({
      next: () => {
        // Notifications fetched successfully
      },
      error: (error) => {
        this._toaster.error(
          error.message || this._transloco.translate('Something went wrong, please try again.'),
        );
      },
    });
  }

  /**
   * Processes the incoming notifications by appending new ones.
   * Handles filtering for duplicate notifications.
   */
  private processNotifications(notifications: Notifications[], isScroll: boolean): void {
    // Handle first-time load or merging new notifications
    if (this._notificationsList.length === 0) {
      this._notificationsList = notifications;
    } else {
      const newNotifications = notifications.filter(
        (notification) =>
          !this._notificationsList.some((existing) => existing.id === notification.id),
      );

      // Prepend new notifications to the list
      if (newNotifications.length > 0) {
        this._notificationsList = [...newNotifications, ...this._notificationsList];
      }
    }

    this.firstLoad = false; // Mark first load as done
  }

  /**
   * Clears the local notifications list.
   */
  private clearNotifications(): void {
    this._notificationsList = [];
  }

  private isCloneRequestNotification(notification: Notifications, pathName: string): boolean {
    const messageText = notification?.data?.text?.toLowerCase() ?? '';
    const notificationDataType = this.getNotificationDataType(notification);

    return (
      pathName.includes('clone-requests') ||
      messageText.includes('clone request') ||
      notificationDataType.includes('clone_request') ||
      !!this.extractCloneRequestId(notification)
    );
  }

  private isSyncUpdateNotification(notification: Notifications, _pathName: string): boolean {
    const notificationDataType = this.getNotificationDataType(notification);
    const validSyncTypes = new Set([
      'floor_sync_created',
      'floor_sync_updated',
      'floor_sync_deleted',
      'point_sync_created',
      'point_sync_updated',
      'point_sync_deleted',
    ]);
    const syncContext = this.extractSyncContext(notification);

    if (validSyncTypes.has(notificationDataType)) {
      return true;
    }

    if (notificationDataType === 'sync_batch') {
      return this.hasBatchSyncUpsertChanges(notification);
    }

    if (
      syncContext &&
      (syncContext.changeType === 'created' ||
        syncContext.changeType === 'updated' ||
        syncContext.changeType === 'deleted')
    ) {
      return true;
    }

    // Support legacy payloads where data.type is missing but data.changes exists.
    if (this.hasBatchSyncUpsertChanges(notification)) {
      return true;
    }

    return false;
  }

  private extractProjectId(notification: Notifications, pathName: string): string | null {
    if (notification?.data?.project_id) {
      return notification.data.project_id;
    }

    const match = pathName.match(/\/(?:project-d|projects)\/([a-zA-Z0-9-]+)/);
    return match?.[1] ?? null;
  }

  private extractCloneRequestId(notification: Notifications, parsedUrl?: URL): string | null {
    const data = notification?.data as Record<string, any>;
    const fromPayload = data?.clone_request_id ?? data?.request_id ?? null;

    if (fromPayload) {
      return String(fromPayload);
    }

    if (parsedUrl) {
      return (
        parsedUrl.searchParams.get('requestId') || parsedUrl.searchParams.get('clone_request_id')
      );
    }

    return null;
  }

  private getNotificationDataType(notification: Notifications): string {
    return ((notification?.data as Record<string, any>)?.type ?? '').toString().toLowerCase();
  }

  private extractSyncContext(notification: Notifications): {
    entityType: 'floor' | 'point';
    changeType: 'created' | 'updated' | 'deleted';
  } | null {
    const data = notification?.data as Record<string, any>;
    const directEntityType = data?.entity_type;
    const directChangeType = data?.change_type;

    if (
      this.isValidSyncEntityType(directEntityType) &&
      this.isValidSyncChangeType(directChangeType)
    ) {
      return {
        entityType: directEntityType,
        changeType: directChangeType,
      };
    }

    const notificationType = this.getNotificationDataType(notification);
    const matches = notificationType.match(/^(floor|point)_sync_(created|updated|deleted)$/);
    if (!matches) {
      return null;
    }

    return {
      entityType: matches[1] as 'floor' | 'point',
      changeType: matches[2] as 'created' | 'updated' | 'deleted',
    };
  }

  private isValidSyncEntityType(value: unknown): value is 'floor' | 'point' {
    return value === 'floor' || value === 'point';
  }

  private isValidSyncChangeType(value: unknown): value is 'created' | 'updated' | 'deleted' {
    return value === 'created' || value === 'updated' || value === 'deleted';
  }

  private hasBatchSyncUpsertChanges(notification: Notifications): boolean {
    const data = notification?.data as Record<string, any>;
    const changes = Array.isArray(data?.changes) ? data.changes : [];

    return changes.some((change: Record<string, unknown>) => {
      const entityType = change?.entity_type;
      const changeType = change?.change_type;

      return (
        this.isValidSyncEntityType(entityType) &&
        (changeType === 'created' || changeType === 'updated' || changeType === 'deleted')
      );
    });
  }

  private async canOpenCloneRequests(
    notification: Notifications,
    projectId: string,
  ): Promise<boolean> {
    const payloadDecision = this.resolveCloneRequestsAccessFromPayload(notification);
    if (payloadDecision !== null) {
      return payloadDecision;
    }

    try {
      const response = await firstValueFrom(this.projectsService.getProjectDetails(projectId));
      const details = response?.project_details;
      return !!details?.is_creator && !details?.shared_by;
    } catch {
      return false;
    }
  }

  private resolveCloneRequestsAccessFromPayload(notification: Notifications): boolean | null {
    const data = notification?.data as unknown as Record<string, unknown>;
    const isSharedProject = this.parseBooleanFlag(data?.shared_by);

    if (isSharedProject === null) {
      return null;
    }

    return !isSharedProject;
  }

  private parseBooleanFlag(value: unknown): boolean | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    if (typeof value === 'boolean') {
      return value;
    }

    if (typeof value === 'number') {
      return value === 1;
    }

    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (normalized === 'true' || normalized === '1') {
        return true;
      }
      if (
        normalized === 'false' ||
        normalized === '0' ||
        normalized === 'null' ||
        normalized === 'none'
      ) {
        return false;
      }
      return true;
    }

    return true;
  }

  private extractRequestingOrganizationId(data: Record<string, unknown>): string | null {
    const directId = data?.requesting_organization_id ?? data?.requestingOrganizationId;
    if (directId !== null && directId !== undefined && directId !== '') {
      return String(directId);
    }

    const nestedOrg = data?.requesting_organization as Record<string, unknown>;
    const nestedId = nestedOrg?.id;
    return nestedId !== null && nestedId !== undefined && nestedId !== '' ? String(nestedId) : null;
  }

  // Clean up interval on component/service destruction
  ngOnDestroy(): void {
    this.stopPeriodicFetching();
  }
}
